import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Search, ShieldCheck, Newspaper } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { LeadCTA } from "@/components/LeadCTA";
import { withLocale } from "@/lib/seo";
import { ARTICLES } from "@/data/knowledge";
import { SERVICES_SEO, SERVICE_MAP_SEO } from "@/data/servicesSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catOf = (a) => a.category || SERVICE_MAP_SEO[a.service]?.category || { en: "Guides", ar: "أدلة" };

export default function KnowledgeCenter() {
  const { t, lang, isAr } = useLang();
  const [articles, setArticles] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    axios.get(`${API}/articles`).then((r) => setArticles(Array.isArray(r.data) && r.data.length ? r.data : ARTICLES)).catch(() => setArticles(ARTICLES));
    axios.get(`${API}/tax-updates`).then((r) => setUpdates((r.data || []).slice(0, 4))).catch(() => setUpdates([]));
  }, []);

  const list = articles || ARTICLES;

  const categories = useMemo(() => {
    const seen = new Map();
    list.forEach((a) => { const c = catOf(a); if (!seen.has(c.en)) seen.set(c.en, c); });
    return Array.from(seen.values());
  }, [list]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return list.filter((a) => {
      const inCat = cat === "all" || catOf(a).en === cat;
      const inQ = !term || `${t(a.title)} ${t(a.excerpt)}`.toLowerCase().includes(term);
      return inCat && inQ;
    });
  }, [list, q, cat, t]);

  const isDefault = cat === "all" && !q.trim();
  const featured = isDefault ? filtered[0] : null;
  const rest = featured ? filtered.slice(1) : filtered;

  const Card = ({ a }) => {
    const c = catOf(a);
    return (
      <Link to={withLocale(lang, `/knowledge/${a.slug}`)} data-testid={`article-${a.slug}`} className="group block h-full rounded-2xl border border-line bg-white p-6 shadow-card hover:shadow-lift transition-shadow">
        <div className="flex items-center gap-2">
          <span className="text-[0.7rem] uppercase tracking-wider text-crimson-600 font-semibold">{t(c)}</span>
          {a.official && <ShieldCheck className="h-3.5 w-3.5 text-brandgreen" />}
        </div>
        <h3 className="font-display text-xl text-navy mt-2 leading-snug">{t(a.title)}</h3>
        <p className="mt-2 text-sm text-muted leading-snug">{t(a.excerpt)}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group-hover:gap-2.5 transition-all">{t({ en: "Read guide", ar: "اقرأ الدليل" })} <ArrowRight className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} /></span>
      </Link>
    );
  };

  return (
    <div className="pt-28 lg:pt-32 pb-24 bg-ivory">
      <Seo
        title={{ en: "Accounting & Tax Knowledge Center | Smart Account Vision", ar: "مركز المعرفة المحاسبية والضريبية" }}
        description={{ en: "Practical UAE VAT, Corporate Tax, e-invoicing and accounting guides that answer the questions businesses actually search for.", ar: "أدلة عملية حول ضريبة القيمة المضافة وضريبة الشركات والفوترة الإلكترونية والمحاسبة في الإمارات." }}
      />
      <section className="container-tnsq">
        <Reveal><span className="kicker gold-line">{t({ en: "Knowledge Center", ar: "مركز المعرفة" })}</span></Reveal>
        <Reveal delay={80}><h1 className="font-display text-4xl sm:text-5xl text-ink mt-4 leading-[1.1] max-w-3xl">{t({ en: "Accounting & Tax guides for UAE businesses", ar: "أدلّة المحاسبة والضرائب للأعمال في الإمارات" })}</h1></Reveal>
        <Reveal delay={120}><p className="mt-4 text-muted text-lg max-w-2xl leading-relaxed">{t({ en: "Clear, practical answers on VAT, Corporate Tax, e-invoicing and bookkeeping — written by an Approved Tax Agent.", ar: "إجابات واضحة وعملية حول ضريبة القيمة المضافة وضريبة الشركات والفوترة الإلكترونية ومسك الدفاتر — بقلم وكيل ضريبي معتمد." })}</p></Reveal>

        {/* Search + categories */}
        <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="relative lg:w-80">
            <Search className={`h-4 w-4 text-muted absolute top-1/2 -translate-y-1/2 ${isAr ? "right-4" : "left-4"}`} />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="knowledge-search"
              placeholder={t({ en: "Search guides…", ar: "ابحث في الأدلة…" })}
              className={`w-full h-11 rounded-full border border-line bg-white focus:outline-none focus:border-navy text-sm ${isAr ? "pr-11 pl-4" : "pl-11 pr-4"}`} />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="knowledge-categories">
            <button onClick={() => setCat("all")} className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${cat === "all" ? "bg-navy text-white" : "bg-white border border-line text-ink/70 hover:border-navy"}`}>{t({ en: "All", ar: "الكل" })}</button>
            {categories.map((c) => (
              <button key={c.en} onClick={() => setCat(c.en)} data-testid={`cat-${c.en}`} className={`px-4 h-9 rounded-full text-sm font-semibold transition-colors ${cat === c.en ? "bg-navy text-white" : "bg-white border border-line text-ink/70 hover:border-navy"}`}>{t(c)}</button>
            ))}
          </div>
        </div>

        {/* Featured */}
        {featured && (
          <Reveal delay={80}>
            <Link to={withLocale(lang, `/knowledge/${featured.slug}`)} data-testid="knowledge-featured"
              className="group mt-8 grid md:grid-cols-[1.1fr_1fr] rounded-[1.5rem] overflow-hidden border border-line bg-navy-deep text-white shadow-card">
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                <span className="text-[0.7rem] uppercase tracking-wider text-gold font-semibold">{t({ en: "Featured guide", ar: "الدليل المميّز" })} · {t(catOf(featured))}</span>
                <h2 className="font-display text-2xl lg:text-3xl mt-3 leading-tight">{t(featured.title)}</h2>
                <p className="mt-3 text-white/70 leading-relaxed">{t(featured.excerpt)}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-gold font-semibold group-hover:gap-3 transition-all">{t({ en: "Read the guide", ar: "اقرأ الدليل" })} <ArrowRight className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} /></span>
              </div>
              <div className="hidden md:block bg-gradient-to-br from-navy to-crimson/40" />
            </Link>
          </Reveal>
        )}

        {/* Grid */}
        <div className="mt-8">
          <h2 className="font-display text-xl text-navy mb-4">{isDefault ? t({ en: "Latest guides", ar: "أحدث الأدلة" }) : t({ en: "Results", ar: "النتائج" })}</h2>
          {rest.length ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="knowledge-grid">
              {rest.map((a, i) => <Reveal key={a.slug} delay={(i % 3) * 60}><Card a={a} /></Reveal>)}
            </div>
          ) : (
            <p className="text-muted">{t({ en: "No guides match your search yet.", ar: "لا توجد أدلة تطابق بحثك بعد." })}</p>
          )}
        </div>

        {/* Latest Tax Updates */}
        {updates.length > 0 && (
          <div className="mt-16">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="font-display text-xl text-navy flex items-center gap-2"><Newspaper className="h-5 w-5 text-crimson" /> {t({ en: "Latest tax updates", ar: "أحدث التحديثات الضريبية" })}</h2>
              <Link to={withLocale(lang, "/tax-updates")} className="text-sm font-semibold text-navy inline-flex items-center gap-1.5 hover:text-crimson transition-colors">{t({ en: "View all", ar: "عرض الكل" })} <ArrowRight className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} /></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="knowledge-updates">
              {updates.map((u) => (
                <Link key={u.id} to={withLocale(lang, "/tax-updates")} className="rounded-2xl border border-line bg-white p-5 shadow-card hover:shadow-lift transition-shadow">
                  <div className="flex items-center gap-2 text-[0.7rem] uppercase tracking-wider text-crimson-600 font-semibold">{u.category}{u.official && <ShieldCheck className="h-3.5 w-3.5 text-brandgreen" />}</div>
                  <h3 className="font-display text-base text-navy mt-2 leading-snug line-clamp-3">{isAr ? (u.title_ar || u.title_en) : (u.title_en || u.title_ar)}</h3>
                  <div className="mt-2 text-xs text-muted" dir="ltr">{(u.date || "").slice(0, 10)}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Related services */}
        <div className="mt-16">
          <h2 className="font-display text-xl text-navy mb-4">{t({ en: "Explore our services", ar: "استكشف خدماتنا" })}</h2>
          <div className="flex flex-wrap gap-2.5" data-testid="knowledge-services">
            {SERVICES_SEO.map((s) => (
              <Link key={s.slug} to={withLocale(lang, `/services/${s.slug}`)} className="px-4 h-10 inline-flex items-center rounded-full bg-white border border-line text-sm font-semibold text-ink/80 hover:border-navy hover:text-navy transition-colors">{t(s.h1)}</Link>
            ))}
          </div>
        </div>

        <LeadCTA />
      </section>
    </div>
  );
}
